import { Box, Typography, Paper, useTheme, Badge } from "@mui/material";
import DataGridMAIQ from "app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ";
import DataGridTypes from "types/DataGridTypes";
import { AddUserModal } from "./AddUserModal";
import { useState } from "react";
import type { MouseEvent } from "react";
import UserDetailsModal from "./UserDetailsModal";
import AddIcon from '@mui/icons-material/Add';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterTrayMAIQ from "app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ";
import ButtonMAIQ from "app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ";
import { useUsersFilterTray } from "../../hooks/useUsersFilterTray";

const UsersTab = () => {
  const [isAddUserModal, setIsAddUserModal] = useState(false);
  const [isUserDetailsModalOpen, setIsUserDetailModalOpen] = useState({
    data: {},
    status: false,
  });
  const [refreshKey, setRefreshKey] = useState(0); // Rerender key
  const [isQuickFilterOpen, setIsQuickFilterOpen] = useState(false);
  const [quickFilterAnchorEl, setQuickFilterAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const {
    categories: quickFilterCategories,
    optionsForActiveCategory: quickFilterOptions,
    selectedByCategory: quickFilterSelectedByCategory,
    onSelectionChange: onQuickFilterSelectionChange,
    onClearAllFilters: onQuickFilterClearAll,
    onClearActiveCategory: onQuickFilterClearActiveCategory,
    onClearAllInPanel: onQuickFilterClearAllInPanel,
    quickFilters,
    totalActiveCount: quickFilterTotalActiveCount,
    activeCategoryId: quickFilterActiveCategoryId,
    setActiveCategoryId: setQuickFilterActiveCategoryId,
    isLoading: quickFilterIsLoading,
  } = useUsersFilterTray();

  const handleAddUserModalOpen = () => {
    setIsAddUserModal(true);
  };

  const handleCellClick = (cell) => {
    if (cell.field === "name") {
      setIsUserDetailModalOpen({
        data: cell.row,
        status: true,
      });
    }
  };

  const handleClose = () => {
    setIsUserDetailModalOpen({
      data: {},
      status: false,
    });
    // setRefreshKey((prevKey) => prevKey + 1); // Refresh the DataGrid
  };

  const handleOpenQuickFilter = (event: MouseEvent<HTMLElement>) => {
    setQuickFilterAnchorEl(event.currentTarget);
    setIsQuickFilterOpen(true);
  };

  const handleCloseQuickFilter = () => {
    setIsQuickFilterOpen(false);
    setQuickFilterAnchorEl(null);
  };

  return (
    <Box 
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: "100%",
        p: 0,
      }}
    >
      <Paper 
        elevation={0}
        sx={{ 
          p: 1.5,
          display: "flex",
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: "space-between",
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderRadius: 1.5,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PeopleAltIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 28 
            }} 
          />
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.primary.main
              }}
            >
              User Management
            </Typography>
            <Typography 
              variant="body2" 
              color="text.primary"
              sx={{ fontSize: '0.85rem' }}
            >
              Create and manage system users
            </Typography>
          </Box>
        </Box>
        
        <ButtonMAIQ
          variant="contained"
          color="primary"
          onClick={handleAddUserModalOpen}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 1,
            fontWeight: 600,
            px: 2.5,
            py: 1,
          }}
        >
          Add New User
        </ButtonMAIQ>
      </Paper>
      
      <Box sx={{ flexGrow: 1, minHeight: 300 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Badge
            badgeContent={
              quickFilterTotalActiveCount > 0 ? quickFilterTotalActiveCount : undefined
            }
            color="primary"
            sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}
          >
            <ButtonMAIQ
              variant="outlined"
              color="primary"
              onClick={handleOpenQuickFilter}
              startIcon={<FilterListIcon />}
              sx={{
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.dark,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              Quick Filters
            </ButtonMAIQ>
          </Badge>
        </Box>
        <DataGridMAIQ 
          key={`${refreshKey}-${JSON.stringify(quickFilters)}`} 
          type={DataGridTypes.USERS_DATA(quickFilters)} 
          onCellClick={handleCellClick} 
        />
      </Box>

      <FilterTrayMAIQ
        isLoading={quickFilterIsLoading}
        open={isQuickFilterOpen}
        onClose={handleCloseQuickFilter}
        anchorEl={quickFilterAnchorEl}
        categories={quickFilterCategories}
        activeCategoryId={quickFilterActiveCategoryId}
        onActiveCategoryChange={setQuickFilterActiveCategoryId}
        optionsForActiveCategory={quickFilterOptions}
        selectedByCategory={quickFilterSelectedByCategory}
        onSelectionChange={onQuickFilterSelectionChange}
        totalActiveCount={quickFilterTotalActiveCount}
        onClearAllFilters={onQuickFilterClearAll}
        onClearActiveCategory={onQuickFilterClearActiveCategory}
        onClearAllInPanel={onQuickFilterClearAllInPanel}
      />

      <AddUserModal
        open={isAddUserModal}
        onClose={() => {
          setIsAddUserModal(false);
          // setRefreshKey((prevKey) => prevKey + 1); // Refresh the DataGrid
        }}
        setRefreshKey={setRefreshKey}
      />
      <UserDetailsModal 
        open={isUserDetailsModalOpen.status} 
        onClose={handleClose} 
        data={isUserDetailsModalOpen.data} 
        setRefreshKey={setRefreshKey} 
      />
    </Box>
  );
};

export default UsersTab;
